// One of the 4 parallel styling sections (header / card / video / about).
// Each writes to seven DB columns prefixed by `prefix`:
//   typography, color_or_img, bg_color, bg_image, bg_repeat, bg_position, bg_size
import type { ProfileUpdate } from "../api/types";
import { ImageUpload } from "./ImageUpload";

const TYPOGRAPHY_PRESETS = [
  "",
  "Editorial Serif (Fraunces)",
  "Modern Sans (General Sans)",
  "Display Wonk (Fraunces · WONK)",
  "Mono (JetBrains)",
];

const REPEAT_OPTIONS = [
  { value: "no-repeat", label: "No repeat" },
  { value: "repeat", label: "Tile both" },
  { value: "repeat-x", label: "Tile horizontally" },
  { value: "repeat-y", label: "Tile vertically" },
];

const POSITION_OPTIONS = [
  { value: "center", label: "Center" },
  { value: "top", label: "Top" },
  { value: "bottom", label: "Bottom" },
  { value: "left", label: "Left" },
  { value: "right", label: "Right" },
  { value: "top left", label: "Top left" },
  { value: "top right", label: "Top right" },
  { value: "bottom left", label: "Bottom left" },
  { value: "bottom right", label: "Bottom right" },
];

const SIZE_OPTIONS = [
  { value: "cover", label: "Cover (fill, may crop)" },
  { value: "contain", label: "Contain (fit, may letterbox)" },
  { value: "auto", label: "Auto (original size)" },
  { value: "100% 100%", label: "Stretch (100% × 100%)" },
];

interface Props {
  prefix: "" | "card_" | "vido_" | "abt_";
  num: string;
  title: string;
  draft: ProfileUpdate;
  onChange: (patch: ProfileUpdate) => void;
}

export function StylingSection({ prefix, num, title, draft, onChange }: Props) {
  const tKey = `${prefix}typography` as keyof ProfileUpdate;
  const modeKey = `${prefix}color_or_img` as keyof ProfileUpdate;
  const colorKey = `${prefix}bg_color` as keyof ProfileUpdate;
  const imageKey = `${prefix}bg_image` as keyof ProfileUpdate;
  const repeatKey = `${prefix}bg_repeat` as keyof ProfileUpdate;
  const positionKey = `${prefix}bg_position` as keyof ProfileUpdate;
  const sizeKey = `${prefix}bg_size` as keyof ProfileUpdate;

  const mode = (draft[modeKey] as string | undefined | null) ?? "bg_color";
  const isImage = mode === "bg_image" || mode === "bg_iamge"; // legacy typo accepted

  const currentColor = (draft[colorKey] as string | null | undefined) ?? "";
  const swatchHex = /^#[0-9a-fA-F]{6}$/.test(currentColor) ? currentColor : "#1A1612";

  return (
    <section className="my-9">
      <div className="mb-6 flex items-baseline gap-5">
        <span className="font-display text-3xl italic leading-none text-terra [font-variation-settings:'opsz'_60,'wght'_300]">
          {num}
        </span>
        <h3 className="text-2xl tracking-tight">{title}</h3>
      </div>

      <div className="rounded-lg border border-rule bg-cream-card p-6">
        {/* Row 1 — typography + bg mode + color/image control */}
        <div className="grid gap-5 md:grid-cols-3">
          <div>
            <label className="field-label" htmlFor={`${prefix}typography`}>
              Typography preset
            </label>
            <select
              id={`${prefix}typography`}
              className="field-input"
              value={(draft[tKey] as string | null | undefined) ?? ""}
              onChange={(e) => onChange({ [tKey]: e.target.value || null } as ProfileUpdate)}
            >
              {TYPOGRAPHY_PRESETS.map((p) => (
                <option key={p || "empty"} value={p}>
                  {p || "— select —"}
                </option>
              ))}
            </select>
          </div>

          <div>
            <span className="field-label">Background</span>
            <div className="mt-1.5 inline-flex rounded-full border border-rule bg-paper-deep p-[3px]">
              <button
                type="button"
                onClick={() => onChange({ [modeKey]: "bg_image" } as ProfileUpdate)}
                className={`rounded-full px-4 py-1.5 font-body text-xs tracking-wide ${
                  isImage ? "bg-ink text-paper" : "text-ink-mute"
                }`}
              >
                Image
              </button>
              <button
                type="button"
                onClick={() => onChange({ [modeKey]: "bg_color" } as ProfileUpdate)}
                className={`rounded-full px-4 py-1.5 font-body text-xs tracking-wide ${
                  !isImage ? "bg-ink text-paper" : "text-ink-mute"
                }`}
              >
                Color
              </button>
            </div>
          </div>

          <div>
            {!isImage && (
              <>
                <label className="field-label" htmlFor={`${prefix}bg_color`}>
                  Background color
                </label>
                <div className="flex items-center gap-3">
                  {/* Native color picker swatch */}
                  <input
                    id={`${prefix}bg_color`}
                    type="color"
                    className="h-10 w-12 cursor-pointer rounded border border-rule bg-cream-card p-0.5"
                    value={swatchHex}
                    onChange={(e) =>
                      // Picking a color implies you want color mode; auto-flip
                      onChange({
                        [colorKey]: e.target.value,
                        [modeKey]: "bg_color",
                      } as ProfileUpdate)
                    }
                  />
                  {/* Editable hex text input */}
                  <input
                    type="text"
                    className="field-input font-mono"
                    value={currentColor}
                    onChange={(e) =>
                      onChange({
                        [colorKey]: e.target.value || null,
                        [modeKey]: "bg_color",
                      } as ProfileUpdate)
                    }
                    placeholder="#F2EBDC"
                  />
                </div>
                {/* Larger preview chip showing the currently saved value */}
                <div
                  className="mt-2 flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.16em] text-ink-mute"
                  aria-hidden="true"
                >
                  <span
                    className="inline-block h-4 w-8 rounded border border-rule"
                    style={{ background: currentColor || "transparent" }}
                  />
                  <span>preview</span>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Image mode — upload + 3 positioning controls */}
        {isImage && (
          <div className="mt-5 space-y-5 border-t border-rule-soft pt-5">
            <ImageUpload
              label="Background image"
              purpose="bg"
              value={(draft[imageKey] as string | null | undefined) ?? null}
              onChange={(url) =>
                onChange({
                  [imageKey]: url,
                  [modeKey]: "bg_image",
                } as ProfileUpdate)
              }
              helper="A wide hero image works best (1600 × 900 or similar)."
            />

            <div className="grid gap-5 md:grid-cols-3">
              <div>
                <label className="field-label" htmlFor={`${prefix}bg_repeat`}>
                  Repeat
                </label>
                <select
                  id={`${prefix}bg_repeat`}
                  className="field-input"
                  value={(draft[repeatKey] as string | null | undefined) ?? "no-repeat"}
                  onChange={(e) => onChange({ [repeatKey]: e.target.value } as ProfileUpdate)}
                >
                  {REPEAT_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="field-label" htmlFor={`${prefix}bg_position`}>
                  Position
                </label>
                <select
                  id={`${prefix}bg_position`}
                  className="field-input"
                  value={(draft[positionKey] as string | null | undefined) ?? "center"}
                  onChange={(e) => onChange({ [positionKey]: e.target.value } as ProfileUpdate)}
                >
                  {POSITION_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="field-label" htmlFor={`${prefix}bg_size`}>
                  Size
                </label>
                <select
                  id={`${prefix}bg_size`}
                  className="field-input"
                  value={(draft[sizeKey] as string | null | undefined) ?? "cover"}
                  onChange={(e) => onChange({ [sizeKey]: e.target.value } as ProfileUpdate)}
                >
                  {SIZE_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
